// ─────────────────────────────────────────────────────────────
// Progress Dashboard – Shared Design Tokens
// Single source of truth for colors, shadows, and chart options
// ─────────────────────────────────────────────────────────────

export const COLORS = {
  primary:   '#6366F1',
  secondary: '#8B5CF6',
  success:   '#10B981',
  warning:   '#F59E0B',
  danger:    '#EF4444',
  info:      '#06B6D4',
  amber:     '#F97316',
  pink:      '#EC4899',

  // Chart series palette
  series: ['#6366F1', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4', '#84CC16', '#EC4899'],

  // Neutral
  text:        '#1E293B',
  textMuted:   '#64748B',
  textLight:   '#94A3B8',
  border:      'rgba(99,102,241,0.12)',
  borderLight: '#E2E8F0',
  bg:          '#F8FAFF',
  cardBg:      '#FFFFFF',
  skeletonBg:  '#F1F5F9',
};

export const GRADIENTS = {
  primary:    'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
  success:    'linear-gradient(135deg, #10B981 0%, #059669 100%)',
  warning:    'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
  danger:     'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
  info:       'linear-gradient(135deg, #06B6D4 0%, #0284C7 100%)',
  teal:       'linear-gradient(135deg, #3f88a5 0%, #2e6b7a 100%)',
  dark:       'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
  cardHeader: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
};

export const SHADOWS = {
  card:   '0 4px 24px rgba(99,102,241,0.10), 0 1px 4px rgba(0,0,0,0.06)',
  cardHover: '0 8px 32px rgba(99,102,241,0.18), 0 2px 8px rgba(0,0,0,0.08)',
  sm:     '0 1px 4px rgba(0,0,0,0.06)',
  glow:   '0 0 20px rgba(99,102,241,0.25)',
};

export const RADII = {
  card:   '16px',
  inner:  '12px',
  badge:  '20px',
  btn:    '8px',
};

// ── Common ApexCharts option fragments ──────────────────────────────────────

export const apexAnimation = {
  enabled: true,
  easing: 'easeinout',
  speed: 800,
  animateGradually: { enabled: true, delay: 100 },
};

export const apexToolbar = { show: false };

export const apexFontFamily = 'Inter, system-ui, Arial, sans-serif';

export const apexLegend = {
  show: true,
  position: 'top',
  horizontalAlign: 'center',
  fontSize: '13px',
  fontWeight: 500,
  fontFamily: apexFontFamily,
  labels: { colors: COLORS.textMuted, useSeriesColors: false },
  markers: { width: 10, height: 10, radius: 50, offsetX: -2 },
  itemMargin: { horizontal: 12, vertical: 6 },
};

export const apexGrid = {
  show: true,
  borderColor: '#EEF2FF',
  strokeDashArray: 4,
  xaxis: { lines: { show: false } },
  yaxis: { lines: { show: true } },
  padding: { top: 0, right: 20, bottom: 0, left: 10 },
};

export const apexTooltipStyle = {
  fontSize: '13px',
  fontFamily: apexFontFamily,
};

export const apexMarkers = {
  size: 5,
  strokeWidth: 2,
  strokeColors: '#fff',
  hover: { size: 7, sizeOffset: 2 },
};

export const apexStroke = { curve: 'smooth', width: 3 };

/** Build a gradient fill config for area/line charts */
export const buildGradientFill = (opacityFrom = 0.35, opacityTo = 0.05) => ({
  type: 'gradient',
  gradient: {
    shade: 'light',
    type: 'vertical',
    shadeIntensity: 0.2,
    opacityFrom,
    opacityTo,
    stops: [0, 100],
  },
});

/** Standard percentage Y-axis */
export const percentageYAxis = (title = 'Percentage (%)') => ({
  title: {
    text: title,
    style: { fontSize: '12px', fontWeight: 500, color: COLORS.textMuted, fontFamily: apexFontFamily },
  },
  labels: {
    formatter: (val) => `${Number(val).toFixed(1)}%`,
    style: { fontSize: '12px', colors: COLORS.textMuted, fontFamily: apexFontFamily },
  },
  min: 0,
  max: 100,
  tickAmount: 5,
});

/** Shared xaxis label style */
export const apexXAxisLabels = {
  style: {
    fontSize: '12px',
    fontWeight: 500,
    colors: COLORS.text,
    fontFamily: apexFontFamily,
  },
};

export const METRIC_CARDS = [
  {
    key: 'totalDataPoints',
    label: 'Total Data Points',
    icon: '🗄️',
    gradient: GRADIENTS.primary,
    accentColor: COLORS.primary,
  },
  {
    key: 'overallCompletion',
    label: 'Overall Completion',
    icon: '✅',
    gradient: GRADIENTS.success,
    accentColor: COLORS.success,
    suffix: '%',
  },
  {
    key: 'activeContributors',
    label: 'Active Contributors',
    icon: '👥',
    gradient: GRADIENTS.info,
    accentColor: COLORS.info,
  },
  {
    key: 'pendingReviews',
    label: 'Pending Reviews',
    icon: '🕒',
    gradient: GRADIENTS.warning,
    accentColor: COLORS.warning,
  },
];

export const ACTIVITY_CONFIG = {
  completed: { gradient: GRADIENTS.success, color: COLORS.success, icon: '✅' },
  progress:  { gradient: GRADIENTS.primary,  color: COLORS.primary,  icon: '🔄' },
  overdue:   { gradient: GRADIENTS.danger,   color: COLORS.danger,   icon: '⚠️' },
  upcoming:  { gradient: GRADIENTS.info,     color: COLORS.info,     icon: '📅' },
};
